import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Card, Table, Button, Form, Row, Col, InputGroup, Dropdown, Badge, Spinner, Modal, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faEye, faEdit, faTrashAlt, faEllipsisV, faSortUp, faSortDown, faSync } from '@fortawesome/free-solid-svg-icons';
import { getMeasurementUnitList, deleteMeasurementUnit } from '../../Controller/MeasurementUnitController';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { DebounceInput } from 'react-debounce-input';
import Pagination from '../../../../../GlobalModules/Utility/Components/Pagination';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';

/**
 * Component for displaying and managing measurement units list
 * @returns {JSX.Element}
 */
const MeasurementUnitList = (props) => {
  const { lng } = useParams();
  const history = useHistory();

  // State for measurement units data
  const [measurementUnits, setMeasurementUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // State for search, filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(localStorage.getItem('measurement_unit_page_size')) || 10
  );

  // State for sorting
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  // Consolidated filters state for API calls
  const [filters, setFilters] = useState({
    limit: parseInt(localStorage.getItem('measurement_unit_page_size')) || 10,
    offset: 0,
    search: '',
    sort: [{
      field: 'id',
      sort: 'desc'
    }]
  });

  // Alert state for delete confirmation
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteItemSlack, setDeleteItemSlack] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  // Fetch measurement units on component mount and when filters change
  useEffect(() => {
    fetchMeasurementUnits();
  }, [filters]);

  const handleRefresh = () => {
    fetchMeasurementUnits();
  };

  /**
   * Fetch measurement units with current filters
   */
  const fetchMeasurementUnits = async () => {
    setLoading(true);
    try {
      const response = await getMeasurementUnitList(filters);

      if (response.success) {
        setMeasurementUnits(response.data || []);
        setTotalItems(response.total || 0);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch measurement units', 'Error', 'error');
        setMeasurementUnits([]);
      }
    } catch (error) {
      console.error('Error fetching measurement units:', error);
      Utility.toastNotifications('An error occurred while fetching measurement units', 'Error', 'error');
      setMeasurementUnits([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle search input change with debounce built into DebounceInput
   */
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on new search
    setFilters(prev => ({
      ...prev,
      search: value,
      offset: 0 // Reset offset when search changes
    }));
  };

  /**
   * Handle column sort
   * @param {string} field - Field to sort by
   */
  const handleSort = (field) => {
    // If clicking the same field, toggle sort direction
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setFilters(prev => ({
        ...prev,
        sort: [{
          field,
          sort: newDirection
        }]
      }));
    } else {
      // If clicking a new field, default to ascending sort
      setSortField(field);
      setSortDirection('asc');
      setFilters(prev => ({
        ...prev,
        sort: [{
          field,
          sort: 'asc'
        }]
      }));
    }
  };

  /**
   * Open delete confirmation modal
   */
  const confirmDelete = (unit) => {
    setDeleteItemSlack(unit.slack);
    setDeleteItemName(unit.label);
    setShowDeleteAlert(true);
  };

  /**
   * Close delete confirmation modal
   */
  const cancelDelete = () => {
    setShowDeleteAlert(false);
    setDeleteItemSlack(null);
    setDeleteItemName('');
  };

  /**
   * Handle delete action
   */
  const handleDelete = async () => {
    if (!deleteItemSlack) return;

    try {
      setLoading(true);

      const response = await deleteMeasurementUnit(deleteItemSlack);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Measurement unit deleted successfully', 'Success', 'success');
        fetchMeasurementUnits(); // Refresh list after delete
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete measurement unit', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting measurement unit:', error);
      Utility.toastNotifications('An error occurred while deleting measurement unit', 'Error', 'error');
    } finally {
      setShowDeleteAlert(false);
      setDeleteItemSlack(null);
      setDeleteItemName('');
      setLoading(false);
    }
  };

  /**
   * Handle page change in pagination
   * @param {number} page - New page number
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * itemsPerPage
    }));
  };

  /**
   * Handle limit change for pagination
   */
  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit);
    setItemsPerPage(newLimit);
    localStorage.setItem('measurement_unit_page_size', newLimit);
    setFilters(prev => ({
      ...prev,
      limit: newLimit,
      offset: 0 // Reset to first page when changing limit
    }));
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const measurementUnitPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'measurement_units',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Check if any actions are available for display in the table
  const hamburgerTableActions = measurementUnitPermissions.view || measurementUnitPermissions.edit || measurementUnitPermissions.delete;

  return (
    <Container fluid>
      <div className="page-header mb-2 top-header-box columnList">
        <div className="page-title" style={{ flexDirection: 'column', alignItems: 'flex-start', margin: 0 }}>
          <h4>Measurement Units</h4>
          <h6 style={{ margin: 0 }}>Manage your measurement units</h6>
        </div>
        <div className="page-btn">
          {measurementUnitPermissions.create && (
            <Link to={`/${lng}/settings/measurement-units/add`} className="btn btn-primary btn-added">
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Measurement Unit
            </Link>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>

          <Row className="mb-0 d-flex justify-content-end">
            <Col md={3} className='mb-3'>
              <InputGroup className='input-group input-group-sm'>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <DebounceInput
                  minLength={2}
                  debounceTimeout={500}
                  className="form-control"
                  placeholder="Search measurement units..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e)}
                />
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={loading}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={loading}
                    />
                  </Button>
                </div>
              </InputGroup>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table className="table-striped">
              <thead>
                <tr>
                  <th onClick={() => handleSort('label')} style={{ cursor: 'pointer' }}>
                    Label {sortField === 'label' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('unit_code')} style={{ cursor: 'pointer' }}>
                    Unit Code {sortField === 'unit_code' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {sortField === 'status' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                    Created On {sortField === 'createdAt' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  {hamburgerTableActions && (
                    <th className="">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 6 : 5} className="text-center py-4">
                      {/* <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-2">Loading measurement units...</p> */}
                    </td>
                  </tr>
                ) : measurementUnits.length === 0 ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 6 : 5} className="text-center py-5 nosearchResult">
                      No measurement units found
                      {searchTerm && (
                        <div className="mt-2">
                          <Button variant="link" onClick={() => {
                            setSearchTerm('');
                            setFilters(prev => ({ ...prev, search: '' }));
                          }}>
                            Clear search
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  measurementUnits.map((unit) => (
                    <tr key={unit.slack}>
                      <td>{unit.label}</td>
                      <td>{unit.unit_code}</td>
                      <td>
                        <Badge
                          bg={unit.status_data?.color || (unit.status === 1 ? 'success' : 'danger')}
                        >
                          {unit.status_data?.label || (unit.status === 1 ? 'Active' : 'Inactive')}
                        </Badge>
                      </td>
                      <td>{Utility.get12HourFormat(unit.createdAt) || 'N/A'}</td>
                      {hamburgerTableActions && (
                        <td className="text-end">
                          <Dropdown align="end">
                            <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${unit.id}`} className="btn-icon no-caret">
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {/* {measurementUnitPermissions?.view && ( */}
                                <Dropdown.Item as={Link} to={`/${lng}/settings/measurement-units/view/${unit.slack}`}>
                                  <FontAwesomeIcon icon={faEye} className="me-2" />
                                  View
                                </Dropdown.Item>
                              {/* )} */}
                              {measurementUnitPermissions?.edit && (
                                <Dropdown.Item as={Link} to={`/${lng}/settings/measurement-units/edit/${unit.slack}`}>
                                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                                  Edit
                                </Dropdown.Item>
                              )}
                              {measurementUnitPermissions?.delete && (
                                <>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    onClick={() => confirmDelete(unit)}
                                    className="text-danger"
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                    Delete
                                  </Dropdown.Item>
                                </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          <Pagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            siblingCount={2}
            onLimitChange={handleLimitChange}
            limitOptions={[10, 25, 50, 100, 500, 1000]}
          />
        </Card.Body>
      </Card>

      {/* Delete Confirmation Alert */}
      {showDeleteAlert && (
        <Modal show={showDeleteAlert} onHide={cancelDelete} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ConfirmationAlert
              BodyFirstContent="Are you sure you want to delete measurement unit:"
              BodySecondContent={deleteItemName}
              BodyThirdContent="?"
              BodyForthContent="This action cannot be undone."
              confirmationButtonContent="Yes, delete it!"
              cancelButtonContent="Cancel"
              deleteIconVissable={true}
              deleteConfirmButton={handleDelete}
              deleteCancleButton={cancelDelete}
            />
          </Modal.Body>
        </Modal>
      )}
    </Container>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token
  };
}

export default connect(mapStateToProps)(withNamespaces()(MeasurementUnitList));

